import { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, Tag, Library, LogOut, ChevronDown, KeyRound, Loader2, Globe, Inbox, CalendarRange, Stamp, HelpCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const NAV = [
  { to: "/", label: "ダッシュボード", icon: LayoutDashboard, end: true },
  { to: "/platform/companies", label: "法人管理", icon: Building2 },
  { to: "/platform/plans", label: "プラン・料金", icon: Tag },
  { to: "/platform/master", label: "研修コンテンツ", icon: Library },
  { to: "/platform/annual-plan", label: "年間計画設定", icon: CalendarRange },
  { to: "/platform/certificate", label: "修了証設定", icon: Stamp },
  { to: "/platform/lp-settings", label: "LP設定", icon: Globe },
  { to: "/platform/contacts", label: "お問い合わせ", icon: Inbox, badge: true },
];

export default function PlatformLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const [unread, setUnread] = useState(0);

  const loadUnread = useCallback(() => {
    api.get("/platform/contacts/unread-count").then(({ data }) => setUnread(data.count || 0)).catch(() => {});
  }, []);
  useEffect(() => {
    loadUnread();
    const id = setInterval(loadUnread, 60000);
    window.addEventListener("wcc:contacts", loadUnread);
    return () => { clearInterval(id); window.removeEventListener("wcc:contacts", loadUnread); };
  }, [loadUnread]);

  const changePw = async () => {
    setBusy(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("パスワードを変更しました");
      setPwOpen(false);
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-950">
      <aside className="hidden lg:flex flex-col w-64 bg-slate-900 border-r border-slate-800 shrink-0">
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-800">
          <img src="/wcc-icon.png" alt="WCC" className="w-9 h-9 rounded-lg object-contain bg-white p-0.5" />
          <div className="leading-tight">
            <div className="font-bold text-white">運営コンソール</div>
            <div className="text-[10px] text-slate-400">Welfy Care Campus 運営</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`platform-nav-${n.to === "/" ? "home" : n.to.split("/").pop()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-amber-500/15 text-amber-400" : "text-slate-300 hover:bg-slate-800"
                }`
              }
            >
              <n.icon className="w-[18px] h-[18px]" />
              <span className="flex-1">{n.label}</span>
              {n.badge && unread > 0 && (
                <span data-testid="platform-contacts-badge" className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-amber-500 text-slate-900 text-[11px] font-bold flex items-center justify-center">{unread}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 text-[10px] text-slate-500 border-t border-slate-800">© 2026 Welfy Care Campus</div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-4 sm:px-6 gap-3 shrink-0">
          <div className="text-sm font-semibold text-slate-200">運営管理コンソール</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/guide")}
              data-testid="platform-header-guide-btn"
              title="使い方ガイド"
              className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-amber-400 rounded-lg px-2.5 py-1.5 hover:bg-slate-800 transition-colors"
            >
              <HelpCircle className="w-[18px] h-[18px]" />
              <span className="hidden sm:inline">使い方</span>
            </button>
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="platform-user-menu" className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-800 transition-colors">
                <div className="w-8 h-8 rounded-full bg-amber-500 text-slate-900 flex items-center justify-center text-sm font-bold">
                  {user.name?.[0]}
                </div>
                <div className="text-left hidden sm:block">
                  <div className="text-sm font-medium text-slate-100 leading-none">{user.name}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{user.role_label}</div>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>{user.name}（{user.role_label}）</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="platform-change-pw">
                <KeyRound className="w-4 h-4 mr-2" /> パスワード変更
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="platform-logout" className="text-rose-600">
                <LogOut className="w-4 h-4 mr-2" /> ログアウト
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 bg-slate-950">
          <div className="max-w-7xl mx-auto wcc-fadein">
            <Outlet />
          </div>
        </main>
      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>パスワード変更</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>現在のパスワード</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-1" data-testid="platform-cur-pw" /></div>
            <div><Label>新しいパスワード（8文字以上）</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-1" data-testid="platform-new-pw" /></div>
          </div>
          <DialogFooter><Button onClick={changePw} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="platform-save-pw">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "変更"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
